import React from 'react';
import dayjs from 'dayjs';
import {
	SypacText,
	SypacVerticalTimeline,
	SypacVerticalTimelineStep,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { NotificationInterface } from '../../OrdersTable/interfaces/Order.interface';
import Default from '../../Notification/Default';
import { Contract } from '../../../services/contracts.service';
import ContractEditedNotification from '../../Notification/ContractEdited';
import { cn } from '../../../lib/utils';
import { ContractNotificationCategory } from '../interfaces/Contract.interface';

interface ContractTimelineProps {
	contract: Contract;
	role: string;
}

type TitleDescription = {
	title: string;
	description?: string | JSX.Element;
} | null;

const getContractNotificationCopy = (
	t: (key: string, defaultValue?: string) => string,
	notification: NotificationInterface,
): TitleDescription => {
	const { category, content } = notification as NotificationInterface & {
		content?: any;
	};

	switch (category) {
		case ContractNotificationCategory.ContractCreated:
			return {
				title: t(
					'contractNotifications.contractCreated.title',
					'Contract created',
				),
				description: t(
					'contractNotifications.contractCreated.description',
					'The contract has been created.',
				),
			};

		case ContractNotificationCategory.OrderLinked: {
			const orderId = content?.orderId;
			return {
				title: t(
					'contractNotifications.orderCreatedFromContract.title',
					'Order created from contract',
				),
				description: orderId ? (
					<T
						keyName="contractNotifications.orderCreatedFromContract.descriptionWithId"
						defaultValue="Order #{orderId} has been created from this contract."
						params={{ orderId }}
					/>
				) : (
					t(
						'contractNotifications.orderCreatedFromContract.description',
						'An order has been created from this contract.',
					)
				),
			};
		}

		case ContractNotificationCategory.ContractCanceled:
			return {
				title: t(
					'contractNotifications.contractCanceled.title',
					'Contract canceled',
				),
				description: t(
					'contractNotifications.contractCanceled.description',
					'The contract has been canceled.',
				),
			};

		case ContractNotificationCategory.ContractResumed:
			return {
				title: t(
					'contractNotifications.contractResumed.title',
					'Contract resumed',
				),
				description: t(
					'contractNotifications.contractResumed.description',
					'The contract has been resumed.',
				),
			};

		case ContractNotificationCategory.ContractEdited:
			return {
				title: t(
					'contractNotifications.contractEdited.title',
					'Contract edited',
				),
				description: t(
					'contractNotifications.contractEdited.description',
					'The contract details have been updated.',
				),
			};

		case ContractNotificationCategory.ContractExpired:
			return {
				title: t(
					'contractNotifications.contractExpired.title',
					'Contract expired',
				),
				description: t(
					'contractNotifications.contractExpired.description',
					'The contract has expired.',
				),
			};

		case ContractNotificationCategory.ContractCompleted:
			return {
				title: t(
					'contractNotifications.contractCompleted.title',
					'Contract completed',
				),
				description: t(
					'contractNotifications.contractCompleted.description',
					'The contract has been completed.',
				),
			};

		case ContractNotificationCategory.OrderFromContractDelivered: {
			const orderId = content?.orderId;
			return {
				title: t(
					'contractNotifications.orderFromContractDelivered.title',
					'Order from contract delivered',
				),
				description: orderId ? (
					<T
						keyName="contractNotifications.orderFromContractDelivered.descriptionWithId"
						defaultValue="Order #{orderId} created from this contract has been delivered."
						params={{ orderId }}
					/>
				) : (
					t(
						'contractNotifications.orderFromContractDelivered.description',
						'An order created from this contract has been delivered.',
					)
				),
			};
		}

		case ContractNotificationCategory.ContractExpiringSoon: {
			const rawDays = content?.daysUntilExpiry;
			const days =
				typeof rawDays === 'string' || typeof rawDays === 'number'
					? Number(rawDays)
					: undefined;

			const expiresAt = content?.expiresAt;
			const formattedExpiryDate = expiresAt
				? dayjs(expiresAt).format('DD.MM.YYYY')
				: null;

			const hasValidDays = typeof days === 'number' && !Number.isNaN(days);

			return {
				title: t(
					'contractNotifications.contractExpiringSoon.title',
					'Contract expiring soon',
				),
				description:
					hasValidDays && formattedExpiryDate ? (
						<T
							keyName="contractNotifications.contractExpiringSoon.descriptionWithDate"
							defaultValue="This contract will expire in {days} {dayLabel} (on {date})."
							params={{
								days,
								dayLabel:
									days === 1
										? t('common.day', 'day')
										: t('paymentPeriod.days', 'days'),
								date: formattedExpiryDate,
							}}
						/>
					) : (
						t(
							'contractNotifications.contractExpiringSoon.descriptionFallback',
							'This contract is close to expiring.',
						)
					),
			};
		}

		default:
			return null;
	}
};

const EventsTab: React.FC<ContractTimelineProps> = ({ contract }) => {
	const { t } = useTranslate();

	const notifications: NotificationInterface[] =
		contract?.notifications?.items ?? [];

	const sortedNotifications = [...notifications].sort(
		(a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
	);

	return (
		<div className="flex flex-col gap-7 px-10 py-[14px] -mt-[15px] pt-[21px] overflow-y-auto scrollbar-custom scroll-smooth">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-60">
					<T keyName="contracts.timeline">Timeline</T>
				</p>
			</SypacText>

			{sortedNotifications.length ? (
				<div className="flex flex-col">
					{sortedNotifications.map((item, index) => {
						const copy = getContractNotificationCopy(t, item);
						if (!copy) return null;

						const date = dayjs(item.createdAt).format('DD.MM.YYYY — HH:mm');

						return (
							<div key={item.id} className="flex flex-row gap-[25px]">
								<SypacVerticalTimeline>
									<SypacVerticalTimelineStep
										isActive={index === 0}
										minWidth={200}
									>
										{date}
									</SypacVerticalTimelineStep>
								</SypacVerticalTimeline>
								<div
									className={cn(
										'h-fit w-full',
										item.category ===
											ContractNotificationCategory.ContractEdited &&
											'mb-[24px]',
									)}
								>
									{item.category ===
									ContractNotificationCategory.ContractEdited ? (
										<ContractEditedNotification
											notification={index === 0}
											title={copy.title}
											description={copy.description! as string}
											changes={(item as any).content?.changes}
										/>
									) : (
										<Default
											notification={index === 0}
											title={copy.title}
											description={copy.description! as string}
										/>
									)}
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="flex justify-center items-center">
					<SypacText variant="body-regular-medium">
						<p className="text-xl text-gray-80">
							<T keyName="producerTimeline.noEventsAtTheMoment">
								No events at the moment.
							</T>
						</p>
					</SypacText>
				</div>
			)}
		</div>
	);
};

export default EventsTab;
